import * as Sentry from "@sentry/react-native";
import { Redirect, router, useLocalSearchParams } from "expo-router";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { StyleSheet, useWindowDimensions } from "react-native";
import Animated, {
  FadeIn,
  FadeOut,
  runOnJS,
  useAnimatedScrollHandler,
} from "react-native-reanimated";
import { Button } from "~/components/Button";
import { Divider } from "~/components/Divider";
import { headerHeight } from "~/components/Header";
import { Icon } from "~/components/Icon";
import { Pressable } from "~/components/Pressable";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { Spinner } from "~/components/Spinner";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { logEvent } from "~/features/analytics/firebaseAnalytics";
import { styleConstants } from "~/style/styleConstants";
import { assert } from "~/utils/assert";
import { shadow } from "~/utils/shadow";
import { useErrorAlert } from "../../components/error-alert";
import { GeneralTermsAndConditionsPdf } from "./GeneralTermsAndConditionsPdf";
import { HorseTermsAndConditionsPdf } from "./HorseTermsAndConditionsPdf";
import { SmsTosConsentErrorView } from "./SmsTosConsentErrorView";
import { useTerms } from "./useTerms";
import type { AnimatedScrollView } from "react-native-reanimated/lib/typescript/component/ScrollView";

const styles = StyleSheet.create({
  topContainer: { paddingTop: 46, paddingBottom: 32 },
  scrollToBottomContainer: {
    display: "flex",
    flexDirection: "row",
    gap: styleConstants.spacingLarge,
    position: "absolute",
    bottom: styleConstants.spacingExtraLarge,
    height: 50,
    width: 257,
    left: "50%", // places left edge of the view at the center of the screen
    marginLeft: -128.5, // moves the view left by half of its width so that it is centered
    paddingVertical: styleConstants.spacingLarge,
    paddingHorizontal: 40,
    backgroundColor: styleConstants.colorBackgroundWhite,
    borderRadius: 30,
    justifyContent: "center",
    alignItems: "center",
    ...shadow(5),
  },
  acceptButton: {
    borderRadius: 30,
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    height: 50,
    alignSelf: "center",
    width: 257,
    backgroundColor: styleConstants.colorBackgroundSuccess,
  },
});

export const SmsTosConsentScreen = observer(function SmsTosConsentScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const errorAlert = useErrorAlert();

  const window = useWindowDimensions();
  const scrollViewRef = React.useRef<AnimatedScrollView>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const isScrollToBottomButtonVisible = isScrolledToBottom === false;

  const scrollHandler = useAnimatedScrollHandler(
    {
      onScroll: (event) => {
        const offsetY = event.contentOffset.y;
        const contentHeight = event.contentSize.height;
        const layoutHeight = event.layoutMeasurement.height;

        const ctaButtonHeight = 50;
        const isScrolledToBottom =
          Math.round(offsetY + layoutHeight) >=
          Math.round(contentHeight - ctaButtonHeight);

        runOnJS(setIsScrolledToBottom)(isScrolledToBottom);
      },
    },
    [],
  );

  assert(token, "Token is required");

  const {
    data: terms,
    isPending,
    isError,
    refetch,
    isFetching,
    error,
  } = useTerms(token);

  if (isPending || isFetching) {
    return (
      <Screen preventScroll withBottomInset withTopInset={false}>
        <View flex centerContent>
          <Spinner />
        </View>
      </Screen>
    );
  }

  if (isError) {
    return (
      <SmsTosConsentErrorView
        error={error}
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  if (terms.alreadyAccepted) {
    return <Redirect href="/(auxiliary)/terms/accepted" />;
  }

  return (
    <Screen
      withBottomInset
      withTopInset={false}
      preventScroll
      colorBackgroundWhite
    >
      <Animated.ScrollView
        ref={scrollViewRef}
        scrollEnabled
        bounces={false}
        onScroll={scrollHandler}
        onContentSizeChange={(_contentWidth, contentHeight) => {
          // Check if the content is smaller than the screen height
          // If it is it means that the user is already at the bottom
          if (contentHeight + headerHeight <= window.height) {
            setIsScrolledToBottom(true);
          } else {
            setIsScrolledToBottom(false);
          }
        }}
      >
        <View
          colorBackgroundLight
          paddingHorizontalExtraLarge
          style={styles.topContainer}
        >
          {terms.type === 2 ? (
            <Text weightRegular sizeExtraLarge>
              <FormattedMessage
                id="8pRmsV"
                defaultMessage="Approval of General Terms for horses"
                description="Title for the SMS TOS consent screen"
              />
            </Text>
          ) : (
            <Text weightRegular sizeExtraLarge>
              <FormattedMessage
                id="oeM2VM"
                defaultMessage="Approval of General Terms for small animals"
                description="Title for the SMS TOS consent screen"
              />
            </Text>
          )}
          <Spacer large />
          <Text colorDark weightLight>
            <FormattedMessage
              id="nCaFya"
              defaultMessage="Before we can examine your animal or provide treatment, we need your approval of our terms. <b>Please read through below and approve</b> before your visit to us."
              description="Description for the SMS TOS consent screen"
              values={{
                b: (chunks: React.ReactNode) => (
                  <Text weightBold>{chunks}</Text>
                ),
              }}
            />
          </Text>
        </View>
        <Spacer extraLarge />

        <View flex>
          {/* TODO: replace hardcoded text with onetrust document */}
          {/* You can find some experiments in git history files named: Experiment_GeneralTermsAndConditionsPdf.web.tsx and GeneralTermsAndConditionsPdf.ts */}
          {terms.type === 2 ? (
            <HorseTermsAndConditionsPdf />
          ) : (
            <GeneralTermsAndConditionsPdf />
          )}
        </View>
        <View paddingHorizontalExtraLarge paddingVerticalExtraLarge>
          <Divider />
          <Spacer medium />
          <Text alignCenter colorDark weightLight sizeSmall>
            <FormattedMessage
              id="iSyUA/"
              defaultMessage="By accepting, you confirm that you have read and accept the terms"
              description="Text explaining that the user should read and accept the terms of service"
            />
          </Text>
          <Spacer large />

          <Button
            style={styles.acceptButton}
            title={intl.formatMessage({
              id: "qw1K8n",
              defaultMessage: "I AGREE",
              description: "Button text to accept the terms of service",
            })}
            onPress={async () => {
              try {
                await logEvent("terms_click_confirm");
              } finally {
                // We navigate to the success screen regardless of the outcome of the tracking event
                // but we wait for the tracking event to finish before navigating
                // so that events are tracked in the correct order
                try {
                  await terms.acceptTermsAndConditions();

                  router.replace("/(auxiliary)/terms/success");
                } catch (error) {
                  console.log("Error while accepting TOS", error);

                  Sentry.captureException(error);

                  errorAlert(error);
                }
              }
            }}
          />
        </View>
      </Animated.ScrollView>

      <ScrollToBottomButton
        scrollViewRef={scrollViewRef}
        isVisible={isScrollToBottomButtonVisible}
      />
    </Screen>
  );
});

function ScrollToBottomButton({
  scrollViewRef,
  isVisible,
}: {
  scrollViewRef: React.RefObject<AnimatedScrollView>;
  isVisible: boolean;
}) {
  if (!isVisible) {
    return null;
  }

  return (
    <Animated.View entering={FadeIn} exiting={FadeOut}>
      <Pressable
        style={({ pressed }) => [
          styles.scrollToBottomContainer,
          {
            backgroundColor: pressed
              ? styleConstants.colorBackgroundLight
              : styleConstants.colorBackgroundWhite,
          },
        ]}
        onPress={() => {
          if (!scrollViewRef.current) {
            console.error("scrollViewRef.current is null");
            return;
          }

          scrollViewRef.current.scrollToEnd({ animated: true });
        }}
      >
        <Icon
          name="down-arrow"
          color={styleConstants.colorTextDark}
          size={14}
        />
        <Text>
          <FormattedMessage
            id="SHAwNy"
            defaultMessage="READ AND ACCEPT"
            description="Text explaining that the user should read and accept the terms of service"
          />
        </Text>
      </Pressable>
    </Animated.View>
  );
}
